import { useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Content,
  ErrorPage,
  Header,
  Page,
  Progress,
} from '@backstage/core-components';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { CookieAuthRefreshProvider } from '@backstage/plugin-auth-react';
import {
  getHostedAppPermission,
  HOSTED_APPLICATIONS_PLUGIN_ID,
} from './hostedApps';

type LoadedApp = { title: string; contentUrl: string };

async function loadApp(
  baseUrl: string,
  fetchImpl: typeof fetch,
  appId: string,
): Promise<LoadedApp | undefined> {
  // react-router decodes %2F in route params, so the id must be re-encoded
  // before it is placed in a URL; otherwise "..%2F.." could reach arbitrary
  // backend paths with the viewer's credentials.
  const encodedId = encodeURIComponent(appId);
  const res = await fetchImpl(`${baseUrl}/${encodedId}`);
  if (res.status === 404) {
    return undefined;
  }
  if (!res.ok) {
    throw new Error(`Failed to load application (${res.status})`);
  }
  const meta: { title: string } = await res.json();
  return { title: meta.title, contentUrl: `${baseUrl}/${encodedId}/content/` };
}

function HostedAppFrame({ appId }: { appId: string }) {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: fetchImpl } = useApi(fetchApiRef);

  const { value, loading, error } = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl(
      HOSTED_APPLICATIONS_PLUGIN_ID,
    );
    return loadApp(baseUrl, fetchImpl, appId);
  }, [discoveryApi, fetchImpl, appId]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <ErrorPage
        status="500"
        statusMessage="Failed to load application"
        additionalInfo={error.message}
      />
    );
  }

  if (!value) {
    return <ErrorPage status="404" statusMessage="Application not found" />;
  }

  return (
    <Page themeId="tool">
      <Header title={value.title} />
      <Content noPadding stretch>
        {/*
          allow-same-origin is required: hosted SPAs use localStorage, which an
          opaque-origin sandbox forbids. But combined with allow-scripts, the
          sandbox is effectively decorative: the framed script can remove its
          own sandbox attribute, and if the Backstage app origin equals the
          backend origin (e.g. the backend serves the frontend) it can read
          window.parent, the parent's storage/DOM and tokens. The real security
          boundaries are (a) a separate origin when the app and backend are on
          different origins, and (b) who is allowed to write to the
          applications container (only vetted first-party builds). See "Trust
          boundary" in docs/hosted-applications-plugin.md.
        */}
        <iframe
          title={value.title}
          src={value.contentUrl}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
          style={{
            width: '100%',
            height: '100%',
            minHeight: '80vh',
            border: 'none',
          }}
        />
      </Content>
    </Page>
  );
}

/**
 * Route guard + cookie setup for /apps/:appId. The permission check here only
 * hides the UI (and blocks direct navigation); the backend enforces the same
 * permission on every content request. The iframe is mounted only inside
 * CookieAuthRefreshProvider, which renders its children once the
 * limited-access cookie has been set -- the iframe's script/style/font
 * requests cannot carry a bearer token and depend on that cookie.
 */
export function HostedAppPage() {
  const { appId } = useParams<{ appId: string }>();

  return (
    <RequirePermission permission={getHostedAppPermission(appId as string)}>
      <CookieAuthRefreshProvider pluginId={HOSTED_APPLICATIONS_PLUGIN_ID}>
        <HostedAppFrame appId={appId as string} />
      </CookieAuthRefreshProvider>
    </RequirePermission>
  );
}
